import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { Subject, Subscription, throwError } from 'rxjs';

import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { ConfirmationComponent } from '@components/confirmation/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { SegmentsService } from '@services/segments/1.0/segments.service';
import { Segment, Tenant } from '@generated/tcea/models';
import { SelectionModel } from '@angular/cdk/collections';
import { MessageType } from '@constants/message/1.0';
import { NgIf, DatePipe } from '@angular/common';
import { MessageComponent } from '../message/1.0/message.component';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ButtonComponent } from '../button/1.0/button.component';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MatCheckbox } from '@angular/material/checkbox';

const defaultFilters = {
  availabilityStart: undefined,
  availabilityEnd: undefined,
  search: '',
  enabled: undefined,
  type: undefined,
};

@Component({
  selector: 'txp-segments-grid',
  templateUrl: './segments-grid.component.html',
  styleUrls: ['./segments-grid.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    NgIf,
    MessageComponent,
    MatFormField,
    MatPrefix,
    MatInput,
    ButtonComponent,
    LoadingComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
    DatePipe,
  ],
})
export class SegmentsGridComponent implements OnInit, OnDestroy {
  messageType = MessageType; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  segments: Segment[] = [];
  dataSource: MatTableDataSource<Segment> = new MatTableDataSource<Segment>();
  dialogRef: MatDialogRef<ConfirmationComponent>;

  itemsList = [];
  isLoading = true;
  loadingMore = false;
  subscription: Subscription;
  displayColumns: string[] = ['select', 'segmentName', 'count', 'createdBy', 'createdDate'];
  isStatusChange: boolean;
  selection = new SelectionModel(true, [], true);
  segmentsReadOnly = true;
  brandId: Tenant['id'];
  loadingError = false;
  enableError = false;
  enableSegmentSuccess = false;
  disableSegmentSuccess = false;
  emptySegmentsList = false;
  archiveError = false;
  archiveSuccess = false;
  page = 0;
  isLastPage = false;
  searchClicked = false;
  emptySearchList = false;
  sort: any = { direction: 'asc', active: 'segmentName' };

  filters = { ...defaultFilters };

  private unsubscribe = new Subject<void>();

  constructor(
    private segmentsService: SegmentsService,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private permissionsService: PermissionsService,
  ) {
    this.segmentsReadOnly = !this.permissionsService.getPermission('coupon-management:write');
  }

  ngOnInit() {
    this.brandId = window.sessionStorage.getItem('brand_id');
    this.dataSource.filterPredicate = (el: Segment, filter: string) =>
      el.segmentName.toLowerCase().includes(filter.toLowerCase());

    if (this.brandId) {
      this.getSegments();
    }
  }

  ngOnDestroy() {
    // unsubscribe so it doesn't keep fetching coupons
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Get all coupons for the given brandId and populate the data source as the data
   * is observed
   *
   * @param brandId - brand ID to pull coupons from
   */
  getSegments(): void {
    this.reset();
    this.isLoading = this.page === 0;
    this.subscription = this.segmentsService
      .get(this.brandId, { query: this.filters, page: this.page, sort: this.sort })
      .subscribe({
        next: (response) => {
          const pages = response.pageMetadata?.totalPages;
          const data = response.content;

          this.isLastPage = this.page + 1 >= pages;
          this.segments = [...this.segments, ...data];
          this.dataSource.data = [...this.segments];
          this.isLoading = false;
          this.loadingMore = false;
          if (!this.dataSource.data.length) {
            this.searchClicked ? (this.emptySearchList = true) : (this.emptySegmentsList = true);
          }
        },
        error: (err) => {
          throwError(err);
          this.loadingError = true;
          this.isLoading = false;
          this.loadingMore = false;
        },
      });
  }

  searchSegments() {
    this.searchClicked = true;
    this.segments = [];
    this.page = 0;
    this.isLastPage = false;
    this.getSegments();
  }

  setSorting({ active, direction }: Sort) {
    this.sort = { direction, field: active };
    this.searchSegments();
  }

  loadMoreSegments() {
    if (this.isLoading || this.isLastPage) {
      return;
    }

    this.loadingMore = true;
    this.page = this.page + 1;
    this.getSegments();
  }

  editSegment(segment) {
    if (this.router.url.includes('coupons')) {
      this.router.navigateByUrl(`/coupons/segments-grid/${segment.id}`, {
        state: { data: segment },
      });
    } else {
      // Get the current URL path
      const currentUrl = this.router.url;
      // Determine the base route
      let baseRoute = currentUrl.split('/segments-grid')[0];
      // Append the new segment ID
      const newUrl = `${baseRoute}/segments-grid/${segment.id}`;

      this.router.navigateByUrl(newUrl, { state: { data: segment } });
    }
  }

  createSegment(): void {
    if (this.router.url.includes('coupons')) {
      this.router.navigateByUrl(`/coupons/segments-grid/new`);
    } else {
      // Get the current URL path
      const currentUrl = this.router.url;
      // Determine the base route
      let baseRoute = currentUrl.split('/segments-grid')[0];
      // Append the new segment ID
      const newUrl = `${baseRoute}/segments-grid/new`;
      this.router.navigateByUrl(newUrl);
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      for (const row of this.dataSource.filteredData) {
        this.selection.select(row);
      }
    }
  }

  filter(value) {
    this.dataSource.filter = value.trim().toLowerCase();
    // apply filter to selected items as well, clearing items that do not match
    this.selection.selected.forEach((selected) => {
      const matchesFilter = this.dataSource.filterPredicate(selected, value);
      if (!matchesFilter) {
        this.selection.deselect(selected);
      }
    });
  }

  resetFilters() {
    this.segments = [];
    this.page = 0;
    this.isLastPage = false;
    this.filters = { ...defaultFilters };
    this.getSegments();
  }

  reset() {
    this.loadingError = false;
    this.emptySegmentsList = false;
    this.emptySearchList = false;
    this.enableError = false;
    this.archiveError = false;
    this.archiveSuccess = false;
    this.enableSegmentSuccess = false;
    this.disableSegmentSuccess = false;
  }
}
